import React from 'react';

import type { Icon as IconType } from '@rover/icons';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph } from '@rover/kibble/core';
import type { ThemeParagraphSizes } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';

import { BannerSeverity } from './BannerSeverity.constants';
import {
  getBackgroundColorForSeverity,
  getIconFillColorForSeverity,
  getIconForSeverity,
} from './BannerSeverity.utils';

export type Props = {
  title?: React.ReactNode;
  children?: React.ReactNode;
  severity?: `${BannerSeverity}`;
  testID?: string;
  containerProps?: React.ComponentProps<typeof Box>;
  icon?: React.ComponentType<IconType>;
  iconFillColor?: string;
  hideIcon?: boolean;
  onClose?: () => void;
  fontSize?: ThemeParagraphSizes | ThemeParagraphSizes[];
};

const defaultProps = {
  severity: BannerSeverity.INFO,
  onDismiss: undefined,
};
const Banner = ({
  title,
  children,
  severity = defaultProps.severity,
  testID = 'banner-block',
  containerProps = {},
  icon = undefined,
  iconFillColor = undefined,
  hideIcon = false,
  onClose = undefined,
  fontSize = '100',
}: Props): JSX.Element | null => {
  const theme = useTheme();
  const backgroundColor = getBackgroundColorForSeverity(severity as BannerSeverity, theme);
  const Icon = icon || getIconForSeverity(severity as BannerSeverity);
  if (!title && !children) return null;
  return (
    <Box
      flexDirection="row"
      justifyContent="space-between"
      p="4x"
      alignItems="flex-start"
      aria-live="polite"
      webProps={{
        role: 'status',
      }}
      rxnProps={{
        accessibilityRole: 'alert',
      }}
      testID={testID}
      {...containerProps}
      sx={{
        backgroundColor,
        borderRadius: '8px',
        ...containerProps?.sx,
      }}
    >
      <Flex flex="1" flexDirection="row">
        {!hideIcon && Icon && (
          <Box pr="3x" height={24}>
            <Icon
              width={24}
              height={24}
              fill={iconFillColor || getIconFillColorForSeverity(severity as BannerSeverity, theme)}
              data-testid="banner-block-icon"
            />
          </Box>
        )}
        <Box flex="1">
          {title && (
            <Flex justifyContent="space-between">
              <Paragraph fontWeight="semibold" size={fontSize} marginBottom="1x">
                {title}
              </Paragraph>
            </Flex>
          )}
          {children && (
            <Paragraph mb="0x" size={fontSize}>
              {children}
            </Paragraph>
          )}
        </Box>
        {onClose && (
          <Box height={24} pl="1x">
            <Button
              onClick={onClose}
              icon={Close}
              size="small"
              aria-label="Close"
              variant="flat"
              sx={{ color: theme.textColor.tertiary.toString(), padding: 0 }}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
export default Banner;
